import {
  CalendarClock, Sparkles, Crown, Loader2, Wand2, Target, Palette, Play,
  Info, ArrowRight, Zap, ImagePlus, Trash2, Check, RefreshCw, Eye, CalendarDays, Lock, Bookmark, Shuffle, WandSparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { mediaUrl } from "../lib/api";
import RemixDialog from "../components/RemixDialog";
import { useDailyStudio } from "../hooks/useDailyStudio";

const DAYS = [
  { id: 0, label: "Sun" }, { id: 1, label: "Mon" }, { id: 2, label: "Tue" },
  { id: 3, label: "Wed" }, { id: 4, label: "Thu" }, { id: 5, label: "Fri" }, { id: 6, label: "Sat" },
];

const Field = ({ label, hint, children }) => (
  <div>
    <label className="text-sm font-medium text-slate-300">{label}</label>
    {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
    <div className="mt-2">{children}</div>
  </div>
);

const Card = ({ icon: Icon, title, desc, children, testid }) => (
  <section data-testid={testid} className="rounded-3xl border border-white/10 bg-[#11141E] p-6 sm:p-7">
    <div className="flex items-start gap-3 mb-5">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#1E2333] text-[#FF6A5E]">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <h2 className="font-display font-bold text-lg leading-tight">{title}</h2>
        {desc && <p className="text-sm text-slate-400 mt-0.5">{desc}</p>}
      </div>
    </div>
    {children}
  </section>
);

export default function DailyVideo() {
  const {
    navigate,
    loading, isPro, options, profile, settings, presets, pastVideos, autofilling, remixTarget, setRemixTarget, canTwiceDaily,
    doAutofill, doGenerateFromPreset,
    savingProfile, savingSettings, running, history, uploadingAsset,
    pending, previewing, approving, calendar, planning, busyEntry, approvingAll,
    assetPreview, setAssetPreview, previewAsset,
    setP, setS, handleAssetUpload, removeAsset, setAssetRole,
    doPlan, doApproveEntry, doRegenEntry, doDeleteEntry, doApproveAll, doAssetPreview,
    doPreview, doApprove, doDiscard, doSaveProfile, doSaveSettings, doRunNow,
  } = useDailyStudio();

  if (loading || !settings) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF6A5E]" />
      </div>
    );
  }

  return (
    <div className="relative max-w-4xl mx-auto px-5 sm:px-8 py-12">
      <div className="pointer-events-none absolute -top-20 right-0 h-80 w-80 rounded-full bg-[#FF453A]/12 blur-[120px]" />

      <div className="mb-2"><span className="eyebrow">Autopilot</span></div>
      <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight flex items-center gap-3">
        Daily Fresh Video
      </h1>
      <p className="text-slate-400 mt-2 max-w-2xl">
        A new, on-brand marketing video made for you automatically — learning from your past videos and brand,
        never repeating itself. <span className="text-slate-300">Consistent brand, different creative.</span>
      </p>

      {!isPro && (
        <div data-testid="daily-pro-upsell" className="mt-6 rounded-2xl border border-[#FF453A]/30 bg-[#FF453A]/10 p-5 flex items-start gap-3">
          <Crown className="h-5 w-5 text-[#FF6A5E] mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold">Daily Fresh Video is a Pro feature</p>
            <p className="text-sm text-slate-400 mt-0.5">Each daily video uses your Pro 1-per-day allowance. Upgrade to switch on autopilot.</p>
          </div>
          <Button data-testid="daily-upgrade-btn" onClick={() => navigate("/pricing")} className="bg-[#FF453A] hover:bg-[#FF5D54] text-white rounded-full font-semibold">
            Upgrade <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      )}

      <div className="mt-8 space-y-6">
        {/* Pending concept awaiting approval */}
        {pending?.concept && (
          <section data-testid="pending-concept-card" className="rounded-3xl border border-[#FF453A]/40 bg-[#FF453A]/[0.07] p-6 sm:p-7">
            <div className="flex items-start gap-3 mb-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FF453A] text-white">
                <Sparkles className="h-5 w-5" />
              </span>
              <div>
                <h2 className="font-display font-bold text-lg leading-tight">Tomorrow's concept — your call</h2>
                <p className="text-sm text-slate-400 mt-0.5">Approve to render, or regenerate for a different idea.</p>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#0B0C10] p-5 space-y-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-[#FF6A5E] font-semibold">Hook</p>
                <p className="text-slate-100 mt-1" data-testid="pending-hook">“{pending.concept.product?.hook}”</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Concept</p>
                <p className="text-slate-300 mt-1 text-sm leading-relaxed" data-testid="pending-summary">{pending.concept.concept_summary}</p>
              </div>
              {pending.concept.whats_different && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">What's different</p>
                  <p className="text-slate-400 mt-1 text-sm leading-relaxed">{pending.concept.whats_different}</p>
                </div>
              )}
              <div className="flex flex-wrap gap-1.5 pt-1">
                {(pending.concept.keywords || []).slice(0, 8).map((k) => (
                  <span key={k} className="rounded-full bg-white/5 px-2.5 py-0.5 text-[11px] text-slate-400">{k}</span>
                ))}
              </div>
            </div>
            <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:justify-end">
              <Button data-testid="pending-discard-btn" onClick={doDiscard} variant="ghost" className="rounded-full text-slate-400 hover:text-white h-11">Discard</Button>
              <Button data-testid="pending-regenerate-btn" onClick={doPreview} disabled={previewing} variant="outline" className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-[#161A26] h-11 disabled:opacity-50">
                {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-1.5" /> Regenerate</>}
              </Button>
              <Button data-testid="pending-approve-btn" onClick={doApprove} disabled={approving} className="bg-[#FF453A] hover:bg-[#FF5D54] text-white rounded-full font-semibold h-11 disabled:opacity-50">
                {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" /> Approve & render</>}
              </Button>
            </div>
          </section>
        )}

        {/* Concept calendar */}
        <Card testid="card-calendar" icon={CalendarDays} title="Concept calendar"
          desc="Plan and pre-approve the week ahead so nothing slips.">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <Switch data-testid="toggle-auto-approve" checked={!!settings.auto_approve} onCheckedChange={(v) => setS("auto_approve", v)} />
              Auto-approve planned concepts <span className="text-xs text-slate-500">(hands-off)</span>
            </label>
            <div className="flex items-center gap-2">
              {calendar.some((e) => e.status === "pending") && (
                <Button data-testid="approve-all-btn" onClick={doApproveAll} disabled={approvingAll} variant="outline" className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-[#161A26] h-10 disabled:opacity-50">
                  {approvingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" /> Approve all</>}
                </Button>
              )}
              <Button data-testid="plan-week-btn" onClick={doPlan} disabled={planning || !isPro} variant="outline" className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-[#161A26] h-10 disabled:opacity-50">
                {planning ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1.5" /> Plan the week</>}
              </Button>
            </div>
          </div>
          {calendar.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">No concepts planned yet. Tap “Plan the week” to generate upcoming ideas you can pre-approve.</p>
          ) : (
            <div className="space-y-3">
              {calendar.map((e) => (
                <div key={e.id} data-testid={`calendar-entry-${e.id}`} className="rounded-2xl border border-white/10 bg-[#0B0C10] p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="rounded-lg bg-[#1E2333] px-2.5 py-1 text-xs font-semibold text-slate-300 whitespace-nowrap">{e.date}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        e.status === "approved" ? "bg-emerald-500/15 text-emerald-400" :
                        e.status === "rendered" ? "bg-[#FF453A]/15 text-[#FF6A5E]" : "bg-amber-400/15 text-amber-300"
                      }`}>{e.status}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {e.status === "pending" && (
                        <Button data-testid={`cal-approve-${e.id}`} onClick={() => doApproveEntry(e.id)} disabled={busyEntry === e.id} size="sm" className="bg-[#FF453A] hover:bg-[#FF5D54] text-white rounded-full h-8 px-3 text-xs">
                          {busyEntry === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="h-3.5 w-3.5 mr-1" /> Pre-approve</>}
                        </Button>
                      )}
                      {e.status !== "rendered" && (
                        <>
                          <button data-testid={`cal-regen-${e.id}`} onClick={() => doRegenEntry(e.id)} disabled={busyEntry === e.id} title="Regenerate" className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1E2333] text-slate-400 hover:text-white transition-colors disabled:opacity-50">
                            <RefreshCw className="h-3.5 w-3.5" />
                          </button>
                          <button data-testid={`cal-delete-${e.id}`} onClick={() => doDeleteEntry(e.id)} title="Remove" className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1E2333] text-slate-400 hover:text-white hover:bg-[#FF453A]/30 transition-colors">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                      {e.status === "rendered" && e.project_id && (
                        <Button data-testid={`cal-view-${e.id}`} onClick={() => navigate(`/video/${e.project_id}`)} size="sm" variant="outline" className="rounded-full border-white/15 h-8 px-3 text-xs">View</Button>
                      )}
                    </div>
                  </div>
                  <p className="mt-2.5 text-sm text-slate-200" data-testid={`cal-hook-${e.id}`}>“{e.concept?.product?.hook}”</p>
                  <p className="mt-1 text-xs text-slate-500 leading-relaxed">{e.concept?.concept_summary}</p>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Brand profile */}
        <Card testid="card-brand-profile" icon={Wand2} title="Brand profile"
          desc="The AI combines this with your past videos to stay on-brand.">
          <div className="mb-5 rounded-2xl border border-white/10 bg-[#0B0C10] p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="autofill-card">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm flex items-center gap-2"><WandSparkles className="h-4 w-4 text-[#FF6A5E]" /> Auto-fill from what you've already made</p>
              <p className="text-xs text-slate-400 mt-0.5">Pull the business, products, audience and signature hook from a preset, a past video, or everything at once.</p>
            </div>
            <Select value="" onValueChange={doAutofill} disabled={autofilling}>
              <SelectTrigger data-testid="sel-autofill" className="bg-[#11141E] border-white/10 h-11 sm:w-72">
                {autofilling ? <span className="inline-flex items-center gap-2 text-slate-300"><Loader2 className="h-4 w-4 animate-spin" /> Filling…</span> : <SelectValue placeholder="Choose a source…" />}
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="history" data-testid="autofill-history">Everything I've made (all past videos)</SelectItem>
                {presets.length > 0 && <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-500">Saved presets</div>}
                {presets.map((p) => <SelectItem key={p.id} value={`preset:${p.id}`} data-testid={`autofill-preset-${p.id}`}>{p.name}</SelectItem>)}
                {pastVideos.length > 0 && <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-500">Past videos</div>}
                {pastVideos.map((v) => <SelectItem key={v.id} value={`video:${v.id}`} data-testid={`autofill-video-${v.id}`}>{v.title || v.product?.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Business name">
              <Input data-testid="bp-name" value={profile.business_name} onChange={(e) => setP("business_name", e.target.value)} placeholder="BrewCraft" className="bg-[#0B0C10] border-white/10 h-11" />
            </Field>
            <Field label="Website">
              <Input data-testid="bp-website" value={profile.website} onChange={(e) => setP("website", e.target.value)} placeholder="https://brewcraft.com" className="bg-[#0B0C10] border-white/10 h-11" />
            </Field>
          </div>
          <div className="mt-5">
            <Field label="What does your business do?">
              <Textarea data-testid="bp-desc" value={profile.business_description} onChange={(e) => setP("business_description", e.target.value)} placeholder="We make compact home espresso machines and grinders for everyday coffee lovers." className="bg-[#0B0C10] border-white/10 min-h-[80px]" />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-5 mt-5">
            <Field label="Products / services">
              <Textarea data-testid="bp-products" value={profile.products_services} onChange={(e) => setP("products_services", e.target.value)} placeholder="Espresso machine, precision grinder, cleaning kit" className="bg-[#0B0C10] border-white/10 min-h-[70px]" />
            </Field>
            <Field label="Target audience">
              <Textarea data-testid="bp-audience" value={profile.target_audience} onChange={(e) => setP("target_audience", e.target.value)} placeholder="Home baristas & coffee enthusiasts, 25-45" className="bg-[#0B0C10] border-white/10 min-h-[70px]" />
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Brand colors / visual identity" hint="e.g. espresso brown, cream, gold accents">
              <Input data-testid="bp-colors" value={profile.brand_colors} onChange={(e) => setP("brand_colors", e.target.value)} placeholder="Espresso brown, cream, warm gold" className="bg-[#0B0C10] border-white/10 h-11" />
            </Field>
          </div>

          <div className="mt-5">
            <Field label="Brand assets" hint="Upload your logo & product photos, then tag each so the AI composes new visuals around them">
              <div className="flex flex-wrap gap-3">
                {(profile.assets || []).map((a) => (
                  <div key={a.path} data-testid={`asset-${a.path.split('/').pop()}`} className="w-24">
                    <div className="relative h-24 w-24 overflow-hidden rounded-xl border border-white/10 bg-[#0B0C10] group">
                      <img src={mediaUrl(a.path)} alt="" className="h-full w-full object-cover" />
                      <button
                        data-testid={`asset-remove-${a.path.split('/').pop()}`}
                        onClick={() => removeAsset(a.path)}
                        className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#FF453A]"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                    <Select value={a.role} onValueChange={(v) => setAssetRole(a.path, v)}>
                      <SelectTrigger data-testid={`asset-role-${a.path.split('/').pop()}`} className="mt-1.5 h-8 bg-[#0B0C10] border-white/10 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(options.asset_roles || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <button
                      data-testid={`asset-preview-${a.path.split('/').pop()}`}
                      onClick={() => doAssetPreview(a.path, a.role)}
                      disabled={previewAsset === a.path}
                      className="mt-1.5 w-full flex items-center justify-center gap-1 rounded-lg bg-[#1E2333] py-1.5 text-[11px] text-slate-300 hover:text-white hover:bg-[#262c3d] transition-colors disabled:opacity-50"
                    >
                      {previewAsset === a.path ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Eye className="h-3 w-3" /> Preview</>}
                    </button>
                  </div>
                ))}
                <label data-testid="asset-upload-label" className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-white/20 bg-[#0B0C10] text-slate-400 hover:border-[#FF453A]/50 hover:text-white transition-colors">
                  {uploadingAsset ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                  <span className="text-[10px]">Add</span>
                  <input data-testid="asset-upload-input" type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={handleAssetUpload} disabled={uploadingAsset} />
                </label>
              </div>
            </Field>
          </div>

          <div className="mt-6 flex justify-end">
            <Button data-testid="save-profile-btn" onClick={doSaveProfile} disabled={savingProfile} className="bg-[#1E2333] hover:bg-[#262c3d] text-white rounded-full h-10">
              {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save brand profile"}
            </Button>
          </div>
        </Card>

        {/* Objective + creativity */}
        <Card testid="card-objective" icon={Target} title="Marketing goal & creativity"
          desc="What today's videos should achieve, and how boldly to explore.">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Marketing objective">
              <Select value={settings.objective} onValueChange={(v) => setS("objective", v)}>
                <SelectTrigger data-testid="sel-objective" className="bg-[#0B0C10] border-white/10 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.objectives.map((o) => <SelectItem key={o.id} value={o.id}>{o.name.split("—")[0].trim()}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Creativity level">
              <Select value={settings.creativity} onValueChange={(v) => setS("creativity", v)}>
                <SelectTrigger data-testid="sel-creativity" className="bg-[#0B0C10] border-white/10 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.creativity.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Custom instructions (optional)" hint='e.g. "Focus this week on our new grinder" or "Drive more website traffic"'>
              <Textarea data-testid="sel-instructions" value={settings.instructions} onChange={(e) => setS("instructions", e.target.value)} placeholder="Tell the AI what to focus on…" className="bg-[#0B0C10] border-white/10 min-h-[70px]" />
            </Field>
          </div>
          <div className="mt-5 rounded-2xl border border-[#FF453A]/25 bg-[#FF453A]/5 p-5" data-testid="daily-preset-card">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-semibold text-sm flex items-center gap-2"><Bookmark className="h-4 w-4 text-[#FF6A5E]" /> Promote a saved preset</p>
                <p className="text-xs text-slate-400 mt-1">Reuse a product you've already made. The autopilot keeps the product and affiliate link, but writes a brand-new angle, narration and visuals every time.</p>
              </div>
            </div>
            <div className="mt-4">
              <Select value={settings.preset_id || "none"} onValueChange={(v) => setS("preset_id", v === "none" ? null : v)}>
                <SelectTrigger data-testid="sel-daily-preset" className="bg-[#0B0C10] border-white/10 h-11"><SelectValue placeholder="No preset — use brand profile" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No preset — use brand profile</SelectItem>
                  {presets.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`daily-preset-opt-${p.id}`}>
                      {p.name}{p.affiliate?.url ? " · affiliate link" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!presets.length && (
                <p className="text-xs text-slate-500 mt-2">No presets yet — save one from any video in your Library or from the Create wizard.</p>
              )}
            </div>
            {settings.preset_id && (
              <Button data-testid="btn-generate-from-preset" onClick={doGenerateFromPreset} disabled={running || !isPro}
                className="mt-3 w-full rounded-full bg-[#FF453A] hover:bg-[#ff5c52] text-white h-11">
                {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
                Generate a fresh video from this preset now
              </Button>
            )}
            <div className="mt-4 flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm">Rotate visual style</p>
                <p className="text-xs text-slate-400">Give every video a different look (anime, comic, photoreal, …) instead of one fixed style</p>
              </div>
              <Switch data-testid="toggle-rotate-styles" checked={!!settings.rotate_styles} onCheckedChange={(v) => setS("rotate_styles", v)} />
            </div>
          </div>
          <div className="mt-5">
            <Field label="This week's theme (optional)" hint="A week-long focus that steers every daily video — e.g. 'new grinder launch'">
              <Input data-testid="input-weekly-theme" value={settings.weekly_theme || ""} onChange={(e) => setS("weekly_theme", e.target.value)} placeholder="e.g. New grinder launch week" className="bg-[#0B0C10] border-white/10 h-11" />
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Script AI model">
              <Select value={settings.model} onValueChange={(v) => setS("model", v)}>
                <SelectTrigger data-testid="sel-model" className="bg-[#0B0C10] border-white/10 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.models.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
        </Card>

        {/* Look & voice */}
        <Card testid="card-look" icon={Palette} title="Look & voice"
          desc="Defaults for every auto-generated video.">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Aspect ratio">
              <Select value={settings.aspect_ratio} onValueChange={(v) => setS("aspect_ratio", v)}>
                <SelectTrigger data-testid="sel-aspect" className="bg-[#0B0C10] border-white/10 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.aspect_ratios.map((a) => <SelectItem key={a.id} value={a.id}>{a.name || a.id}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Visuals">
              <Select value={settings.visual_source} onValueChange={(v) => setS("visual_source", v)}>
                <SelectTrigger data-testid="sel-visual-source" className="bg-[#0B0C10] border-white/10 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ai">AI generated</SelectItem>
                  <SelectItem value="stock">Stock photos</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Voice">
              <Select value={settings.voice} onValueChange={(v) => setS("voice", v)}>
                <SelectTrigger data-testid="sel-voice" className="bg-[#0B0C10] border-white/10 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.voices.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Caption style">
              <Select value={settings.caption_style_id} onValueChange={(v) => setS("caption_style_id", v)}>
                <SelectTrigger data-testid="sel-caption" className="bg-[#0B0C10] border-white/10 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.caption_styles.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="mt-5 rounded-2xl border border-white/10 bg-[#0B0C10] p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-[#FF6A5E]" />
                <div>
                  <p className="font-semibold text-sm">Brand voice lock</p>
                  <p className="text-xs text-slate-400">Force one tone on every video (voice above is always used)</p>
                </div>
              </div>
              <Switch data-testid="toggle-lock-tone" checked={!!settings.lock_tone} onCheckedChange={(v) => setS("lock_tone", v)} />
            </div>
            {settings.lock_tone && (
              <div className="mt-4">
                <Select value={settings.locked_tone} onValueChange={(v) => setS("locked_tone", v)}>
                  <SelectTrigger data-testid="sel-locked-tone" className="bg-[#11141E] border-white/10 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {options.tones.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </Card>

        {/* Schedule */}
        <Card testid="card-schedule" icon={CalendarClock} title="Schedule"
          desc="When should we make your video?">
          <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-[#0B0C10] px-5 py-4">
            <div>
              <p className="font-semibold">Daily Video autopilot</p>
              <p className="text-sm text-slate-400">{settings.enabled ? "On — videos generate on your schedule" : "Off"}</p>
            </div>
            <Switch data-testid="toggle-enabled" checked={settings.enabled} disabled={!isPro}
              onCheckedChange={(v) => setS("enabled", v)} />
          </div>

          <div className="mt-5 grid sm:grid-cols-2 gap-5">
            <Field label="Frequency">
              <Select value={settings.frequency} onValueChange={(v) => setS("frequency", v)}>
                <SelectTrigger data-testid="sel-frequency" className="bg-[#0B0C10] border-white/10 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.frequencies.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label={settings.videos_per_day === 2 ? "Morning video (UTC)" : "Time (UTC)"} hint="Generated at the first slot at/after this time">
              <Input data-testid="input-time" type="time" value={settings.custom_time} onChange={(e) => setS("custom_time", e.target.value)} className="bg-[#0B0C10] border-white/10 h-11" />
            </Field>
          </div>

          <div className="mt-5 grid sm:grid-cols-2 gap-5" data-testid="videos-per-day-block">
            <Field label="Videos per day" hint={canTwiceDaily ? "Two slots: a morning and an evening video, each with a fresh concept" : "Two videos a day is included with Creator Plus"}>
              <Select value={String(settings.videos_per_day || 1)} onValueChange={(v) => setS("videos_per_day", Number(v))}>
                <SelectTrigger data-testid="sel-videos-per-day" className="bg-[#0B0C10] border-white/10 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 video a day</SelectItem>
                  <SelectItem value="2" disabled={!canTwiceDaily} data-testid="opt-two-per-day">
                    2 videos a day — morning + evening{!canTwiceDaily ? " (Creator Plus)" : ""}
                  </SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {settings.videos_per_day === 2 && (
              <Field label="Evening video (UTC)" hint="Second slot — runs only after the morning video is done">
                <Input data-testid="input-second-time" type="time" value={settings.second_time || "18:00"} onChange={(e) => setS("second_time", e.target.value)} className="bg-[#0B0C10] border-white/10 h-11" />
              </Field>
            )}
          </div>

          {settings.frequency === "custom" && (
            <div className="mt-5" data-testid="custom-days">
              <label className="text-sm font-medium text-slate-300">Days</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {DAYS.map((d) => {
                  const on = (settings.custom_days || []).includes(d.id);
                  return (
                    <button
                      key={d.id}
                      data-testid={`day-${d.id}`}
                      onClick={() => setS("custom_days", on ? settings.custom_days.filter((x) => x !== d.id) : [...(settings.custom_days || []), d.id])}
                      className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${on ? "bg-[#FF453A] text-white" : "bg-[#0B0C10] border border-white/10 text-slate-400 hover:text-white"}`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-5 flex items-center justify-between rounded-2xl border border-white/10 bg-[#0B0C10] px-5 py-4">
            <div>
              <p className="font-semibold">Review before rendering</p>
              <p className="text-sm text-slate-400">Get tomorrow's concept to approve or regenerate — nothing renders until you say go</p>
            </div>
            <Switch data-testid="toggle-approval" checked={!!settings.require_approval}
              onCheckedChange={(v) => setS("require_approval", v)} />
          </div>

          <div className="mt-5 flex items-center justify-between rounded-2xl border border-white/10 bg-[#0B0C10] px-5 py-4">
            <div>
              <p className="font-semibold">Weekly performance digest</p>
              <p className="text-sm text-slate-400">Get a Monday email recapping your top videos and what worked</p>
            </div>
            <Switch data-testid="toggle-digest" checked={settings.digest_enabled !== false}
              onCheckedChange={(v) => setS("digest_enabled", v)} />
          </div>

          {settings.digest_enabled !== false && (
            <div className="mt-4 grid sm:grid-cols-2 gap-5" data-testid="digest-schedule">
              <Field label="Digest day">
                <Select value={String(settings.digest_day ?? 1)} onValueChange={(v) => setS("digest_day", parseInt(v))}>
                  <SelectTrigger data-testid="sel-digest-day" className="bg-[#0B0C10] border-white/10 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DAYS.map((d) => <SelectItem key={d.id} value={String(d.id)}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Digest time (UTC)">
                <Input data-testid="input-digest-time" type="time" value={settings.digest_time || "09:00"} onChange={(e) => setS("digest_time", e.target.value)} className="bg-[#0B0C10] border-white/10 h-11" />
              </Field>
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:justify-end">
            <Button data-testid="preview-concept-btn" onClick={doPreview} disabled={previewing || !isPro} variant="outline" className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-[#161A26] h-11 disabled:opacity-50">
              {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Eye className="h-4 w-4 mr-1.5" /> Preview next concept</>}
            </Button>
            <Button data-testid="save-settings-btn" onClick={doSaveSettings} disabled={savingSettings} variant="outline" className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-[#161A26] h-11">
              {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save schedule"}
            </Button>
            <Button data-testid="run-now-btn" onClick={doRunNow} disabled={running || !isPro} className="bg-[#FF453A] hover:bg-[#FF5D54] text-white rounded-full font-semibold h-11 disabled:opacity-50">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Zap className="h-4 w-4 mr-1.5" /> Generate now</>}
            </Button>
          </div>
          <p className="mt-3 text-xs text-slate-500 flex items-center gap-1.5">
            <Info className="h-3.5 w-3.5" /> Each generated video counts as your Pro video for that day and is saved to your Library.
          </p>
        </Card>

        {/* History */}
        {history.length > 0 && (
          <Card testid="card-history" icon={Sparkles} title="Recent daily videos"
            desc="Every concept is logged so the AI never repeats itself.">
            <div className="space-y-3">
              {history.map((p) => (
                <div key={p.id} className="relative">
                  {p.status === "completed" && (
                    <button
                      data-testid={`daily-remix-${p.id}`}
                      onClick={() => setRemixTarget(p)}
                      title="Remix — new narration, angle & visual style"
                      className="absolute right-12 top-1/2 -translate-y-1/2 z-10 inline-flex items-center gap-1.5 rounded-full border border-[#FF453A]/40 bg-[#FF453A]/10 px-3 py-1.5 text-xs font-medium text-[#FF6A5E] hover:bg-[#FF453A]/25 hover:text-white transition-colors"
                    >
                      <Shuffle className="h-3.5 w-3.5" /> Remix
                    </button>
                  )}
                <button
                  data-testid={`daily-history-${p.id}`}
                  onClick={() => navigate(`/video/${p.id}`)}
                  className="w-full text-left flex items-center gap-4 rounded-2xl border border-white/10 bg-[#0B0C10] p-3 pr-28 hover:border-white/25 transition-colors"
                >
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-[#1E2333]">
                    {p.thumb_path ? <img src={mediaUrl(p.thumb_path)} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full grid place-items-center"><Play className="h-4 w-4 text-slate-500" /></div>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold truncate">{p.title}</p>
                    <p className="text-xs text-slate-400 truncate">{p.daily_meta?.concept_summary || p.product?.hook}</p>
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-500">
                      <span className="rounded-full bg-white/5 px-2 py-0.5">{p.daily_meta?.objective || "—"}</span>
                      <span className="capitalize">{p.status}</span>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-500" />
                </button>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      <RemixDialog video={remixTarget} open={!!remixTarget} onOpenChange={(o) => { if (!o) setRemixTarget(null); }} />

      {assetPreview && (
        <div
          data-testid="asset-preview-modal"
          onClick={() => setAssetPreview(null)}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6"
        >
          <div onClick={(e) => e.stopPropagation()} className="max-w-sm w-full rounded-3xl border border-white/10 bg-[#11141E] p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="font-display font-bold">Preview in a scene</p>
              <button data-testid="asset-preview-close" onClick={() => setAssetPreview(null)} className="text-slate-400 hover:text-white">✕</button>
            </div>
            <img src={assetPreview.url} alt="Asset in a generated scene" className="w-full rounded-2xl" />
            <p className="mt-3 text-xs text-slate-500">A sample of how the AI composes new visuals around this asset. Actual daily videos vary each day.</p>
          </div>
        </div>
      )}
    </div>
  );
}
